// Shared constants used by both client and server. Keep authoritative here.
export const TICK_RATE = 30;
export const TICK_DT = 1 / TICK_RATE;
export const PATCH_RATE_MS = 50; // ~20 Hz Colyseus state snapshots
export const DEFAULT_SERVER_PORT = 2567; // actual port read from env on the server
export const INTERP_DELAY_MS = 100; // client render lag for interpolation
export const MAX_PLAYERS = 8;
export const TANK = {
    MAX_SPEED: 12,
    ACCEL: 40,
    TURN_SPEED: 2.2, // rad/s
    RADIUS: 1.4,
    MAX_HP: 100,
    RESPAWN_MS: 3000,
    /** Brief invulnerability on (re)spawn so you can't be instakilled before you
     * orient — especially important in coop where a fresh wave can focus you. */
    SPAWN_INVULN_MS: 1500,
    /** Muzzle (barrel tip) offsets, turret-local: forward along aim + height. */
    MUZZLE_FORWARD: 3.0,
    MUZZLE_Y: 1.5,
    /** Distinct per-player colors — NO red (red is reserved for enemy bots). */
    COLOR_BY_INDEX: [
        0x22d3ee, 0x34d399, 0xfb923c, 0x3b82f6,
        0xa855f7, 0xfacc15, 0xec4899, 0x14b8a6,
    ],
};
/** Enemy (bot) color — always red so bots read as hostile vs the player palette. */
export const ENEMY_COLOR = 0xb91c1c;
export const PROJECTILE = {
    SPEED: 38,
    RADIUS: 0.25,
    DAMAGE: 20,
    TTL_MS: 3000,
    COOLDOWN_MS: 500,
};
export const POWERUP = {
    RESPAWN_MS: 15000, // delay before a picked-up power-up reappears
    EFFECT_MS: 6000, // duration of speed / rapid
    HEAL_AMOUNT: 40,
    SPEED_MULT: 1.6, // max-speed multiplier while speed boost active
    COOLDOWN_MULT: 0.4, // fire cooldown multiplier while rapid active (2.5× faster)
    PICKUP_RADIUS: 1.8,
    SHIELD_MS: 4000, // temporary invulnerability window granted by the shield pickup
};
/**
 * TankState `effects` is a shared bitmask replicated to clients. Power-ups own
 * the low bits; class abilities own the high bits. Keep these stable — both
 * server (sets them) and client (reads them for visuals/HUD) depend on them.
 *
 *   1  SPEED     (power-up)   2  RAPID      (power-up)
 *   4  BULWARK   (Heavy spec) 8  INVULN     (Scout dash, also the shield power-up)
 *   16 REPAIRING (Balanced spec)
 */
export const EFFECT_BIT = {
    SPEED: 1,
    RAPID: 2,
    BULWARK: 4,
    INVULN: 8,
    REPAIRING: 16,
};
/** Tuning for the four active class abilities (server-applied, shared for HUD). */
export const ABILITY = {
    REPAIR_INSTANT: 25, // hp restored the moment Repair fires
    REPAIR_HOT_TOTAL: 18, // additional hp restored over REPAIR_DURATION_MS
    REPAIR_DURATION_MS: 3000,
    BULWARK_DURATION_MS: 4000,
    BULWARK_DAMAGE_MULT: 0.5, // incoming damage multiplier while active
    DASH_DISTANCE: 6.5, // forward blink distance (m)
    DASH_INVULN_MS: 500,
    MORTAR_RANGE: 22, // max distance from caster to the aim point (m)
    MORTAR_MIN_RANGE: 6, // can't mortar right on top of yourself
    MORTAR_TRAVEL_MS: 850, // shell air-time before it detonates
    MORTAR_RADIUS: 5.5, // blast radius (m)
    MORTAR_DAMAGE: 55, // damage at the epicenter (falls off to MORTAR_FALLOFF_MIN at the edge)
    MORTAR_FALLOFF_MIN: 0.35,
};
// Fixed power-up spawn points, clear of obstacles. Phase 2: original six
// rescaled 1.75x for the bigger arena, plus two new mid-field spots.
export const POWERUP_POINTS = [
    { x: 0, z: -21 },
    { x: 0, z: 21 },
    { x: -26, z: 26 },
    { x: 26, z: -26 },
    { x: -44, z: 0 },
    { x: 44, z: 0 },
    { x: -15, z: -15 },
    { x: 15, z: 15 },
];
export const DEATHMATCH = {
    FRAG_LIMIT: 10,
    // Bumped alongside the Phase 2 map size increase — a bigger arena means
    // longer travel between fights, so the clock needs more room too.
    TIME_LIMIT_MS: 240_000,
};
/**
 * King of the Hill: a single fixed capture zone. Whoever is the SOLE tank
 * inside it accrues score over time (ties/empty = no one scores); first to
 * WIN_SCORE or the highest score when the clock runs out wins. Center chosen
 * for maximum clearance from every obstacle/building/spawn/power-up on the
 * shared map (~9.5m of open ground past the zone's own edge in every
 * direction) so the fight happens in the open, not inside/against cover.
 */
export const HILL = {
    X: -24,
    Z: 0,
    RADIUS: 8,
    SCORE_PER_SEC: 10,
    WIN_SCORE: 150,
    TIME_LIMIT_MS: 240_000,
};
export const COOP = {
    WAVE_BASE: 2, // bots in the first wave (gentle — ramps up after)
    WAVE_GROWTH: 1, // bots added per wave
    WAVE_BREAK_MS: 5000,
    BOT_HP: 60,
    // Deliberately NOT scaled 1:1 with ARENA.HALF — bots should still close in
    // before opening fire (reaction time, no cross-map sniping), just with a
    // little more room than on the old, smaller map.
    BOT_FIRE_RANGE: 30,
    BOT_FIRE_COOLDOWN_MS: 1400,
    BOT_SPEED: 7, // unused (bot speed is TANK_CLASSES.bot.maxSpeed) — kept for reference
    // ── Difficulty scaling (per wave, wave 1 = baseline/no bonus) ──────────
    // Applied as a multiplier to the picked bot class's base stats at spawn
    // time (see scaleBotClass in ArenaRoom) — bots don't just get more
    // numerous each wave, each one individually gets tougher/faster/deadlier,
    // capped so a long coop run doesn't turn into an unkillable bullet sponge.
    DIFFICULTY_HP_PER_WAVE: 0.1,
    DIFFICULTY_HP_CAP: 2.2,
    DIFFICULTY_DAMAGE_PER_WAVE: 0.06,
    DIFFICULTY_DAMAGE_CAP: 1.8,
    DIFFICULTY_FIRE_RATE_PER_WAVE: 0.05, // cooldown shrinks (fires faster)
    DIFFICULTY_FIRE_RATE_FLOOR: 0.55, // cooldown never drops below 55% of base
    DIFFICULTY_SPEED_PER_WAVE: 0.04,
    DIFFICULTY_SPEED_CAP: 1.4,
    // Aim jitter: bots start sloppy (wide random offset added to their perfect
    // aim angle) and sharpen up wave over wave until they're reliably accurate
    // — "they get better", not just "there are more of them".
    AIM_JITTER_BASE_RAD: 0.3, // wave 1 aim error, ~±17°
    AIM_JITTER_FLOOR_RAD: 0.02, // near-perfect but never quite aimbot-perfect
    AIM_JITTER_SHARPEN_WAVES: 7, // waves to go from base to floor
    // Variant unlocks: once unlocked, each new bot has a chance to roll that
    // variant instead of the plain grunt (checked ricochet-first so late-game
    // waves can occasionally roll both being harder in different ways).
    RICOCHET_UNLOCK_WAVE: 4,
    RICOCHET_CHANCE: 0.3,
    FAST_UNLOCK_WAVE: 2,
    FAST_CHANCE: 0.35,
};
export const ARENA = {
    HALF: 70, // square arena half-extent (meters) — Phase 2: was 40
    WALL_HEIGHT: 4,
    WALL_THICKNESS: 1,
};
/**
 * Spawn points — clear of walls and obstacles, spread around the arena.
 * Cycled by join order so players don't stack on each other.
 * Scaled 1.75x from the original 40-half arena for the Phase 2 map size bump.
 */
export const SPAWN_POINTS = [
    { x: -52, z: -52 },
    { x: 52, z: -52 },
    { x: -52, z: 52 },
    { x: 52, z: 52 },
    { x: 0, z: -58 },
    { x: 0, z: 58 },
    { x: -58, z: 0 },
    { x: 58, z: 0 },
];
/**
 * Static obstacle layout. Shared so the server (colliders) and client (meshes)
 * always agree. Coordinates are arena-space (origin = center). Phase 2: the
 * original seven rescaled 1.75x for the bigger arena, plus four new outer-ring
 * obstacles (placed at least ~20m from every spawn/other obstacle) so the
 * extra space reads as tactical cover, not empty grass.
 */
export const OBSTACLES = [
    { x: -28, z: -21, w: 6, d: 6, h: 3, kind: "sandbags" },
    { x: 28, z: 21, w: 6, d: 6, h: 3, kind: "rocks" },
    { x: 0, z: 0, w: 12, d: 2, h: 3, kind: "concrete" },
    { x: -39, z: 35, w: 4, d: 10, h: 3, kind: "crates" },
    { x: 39, z: -35, w: 4, d: 10, h: 3, kind: "sandbags" },
    { x: 0, z: -42, w: 8, d: 4, h: 3, kind: "rocks" },
    { x: 0, z: 42, w: 8, d: 4, h: 3, kind: "concrete" },
    { x: 17, z: 42, w: 6, d: 6, h: 3, kind: "crates" },
    { x: 42, z: -17, w: 8, d: 4, h: 3, kind: "sandbags" },
    { x: -17, z: -42, w: 4, d: 8, h: 3, kind: "concrete" },
    { x: -42, z: 17, w: 6, d: 6, h: 3, kind: "rocks" },
    // Phase 3: three real, walkable buildings — each a hollow rectangular shell
    // built from 5 separate thin wall segments (not one bounding box), so the
    // collider actually traces the building's footprint: tanks can drive
    // through the door gap and hide/fight inside, and peek around real corners.
    // Enlarged + widened doors (2026-07-01 pass, playtest feedback: driving
    // in/out required clipping precision) so entering/exiting is comfortable
    // for every tank class. Both A/B centers were placed to clear every spawn/
    // obstacle/power-up by construction (B is A mirrored through the origin) —
    // re-verified at this larger footprint.
    // Building A — center (22,-38), 14x12 footprint, 4m walls, 5m-wide door south.
    { x: 22, z: -32.5, w: 14, d: 1, h: 4, kind: "wall" }, // north wall
    { x: 17.25, z: -43.5, w: 4.5, d: 1, h: 4, kind: "wall" }, // south wall, left of door
    { x: 26.75, z: -43.5, w: 4.5, d: 1, h: 4, kind: "wall" }, // south wall, right of door
    { x: 28.5, z: -38, w: 1, d: 12, h: 4, kind: "wall" }, // east wall
    { x: 15.5, z: -38, w: 1, d: 12, h: 4, kind: "wall" }, // west wall
    // Building B — center (-22,38), same footprint, 5m-wide door north.
    { x: -22, z: 32.5, w: 14, d: 1, h: 4, kind: "wall" }, // south wall
    { x: -26.75, z: 43.5, w: 4.5, d: 1, h: 4, kind: "wall" }, // north wall, left of door
    { x: -17.25, z: 43.5, w: 4.5, d: 1, h: 4, kind: "wall" }, // north wall, right of door
    { x: -28.5, z: 38, w: 1, d: 12, h: 4, kind: "wall" }, // west wall
    { x: -15.5, z: 38, w: 1, d: 12, h: 4, kind: "wall" }, // east wall
    // Building C — a taller watchtower (9x9 footprint, 5m walls) for silhouette
    // variety against the two larger buildings. Center (13,-13), 4m-wide door
    // south; verified clear of the center obstacle and both nearby power-ups.
    { x: 13, z: -9, w: 9, d: 1, h: 5, kind: "wall" }, // north wall
    { x: 9.75, z: -17, w: 2.5, d: 1, h: 5, kind: "wall" }, // south wall, left of door
    { x: 16.25, z: -17, w: 2.5, d: 1, h: 5, kind: "wall" }, // south wall, right of door
    { x: 17, z: -13, w: 1, d: 9, h: 5, kind: "wall" }, // east wall
    { x: 8.5, z: -13, w: 1, d: 9, h: 5, kind: "wall" }, // west wall
];
export const BUILDINGS = [
    { x: 22, z: -38, w: 14, d: 12, wallH: 4, doorSide: "s", doorCenter: 22, doorWidth: 5 },
    { x: -22, z: 38, w: 14, d: 12, wallH: 4, doorSide: "n", doorCenter: -22, doorWidth: 5 },
    { x: 13, z: -13, w: 9, d: 9, wallH: 5, doorSide: "s", doorCenter: 13, doorWidth: 4 },
];
//# sourceMappingURL=constants.js.map